import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2, X, Search } from '@/components/icons';
import { SectionHeader } from '@/components/ui/SectionHeader';
import { Button } from '@/components/ui/Button';
import { Table } from '@/components/ui/Table';
import { Input } from '@/components/form/Input';
import { FilterBar, FilterField } from '@/components/ui/FilterBar';
import { useToast } from '@/components/ui/useToast';
import { useConfirm } from '@/components/ui/useConfirm';
import { apiError } from '@/lib/apiError';
import { formatINR } from '@/lib/format';
import { listHsnGst, setHsnGstBands, deleteHsnGst, type HsnGstEntry } from '@/api/hsnGst';

interface BandRow { minPrice: string; maxPrice: string; gstPct: string }
interface Draft { hsnCode: string; isNew: boolean; bands: BandRow[] }

const emptyBand: BandRow = { minPrice: '0', maxPrice: '', gstPct: '' };

export function HsnGstMasterPage() {
  const qc = useQueryClient();
  const toast = useToast();
  const confirmDlg = useConfirm();
  const [draft, setDraft] = useState<Draft | null>(null);

  const query = useQuery({ queryKey: ['hsn-gst'], queryFn: listHsnGst });
  const rows = query.data ?? [];
  const [search, setSearch] = useState('');

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => r.hsnCode.toLowerCase().includes(q) || (r.description ?? '').toLowerCase().includes(q));
  }, [rows, search]);

  const saveMut = useMutation({
    mutationFn: (d: Draft) =>
      setHsnGstBands(
        d.hsnCode.trim(),
        d.bands
          .filter((b) => b.gstPct !== '')
          .map((b) => ({
            minPrice: Number(b.minPrice || 0),
            maxPrice: b.maxPrice.trim() === '' ? null : Number(b.maxPrice),
            gstPct: Number(b.gstPct),
          })),
      ),
    onSuccess: () => {
      toast.push('HSN GST bands saved', 'success');
      setDraft(null);
      qc.invalidateQueries({ queryKey: ['hsn-gst'] });
    },
    onError: (e) => toast.push(apiError(e, 'Save failed'), 'error'),
  });

  const delMut = useMutation({
    mutationFn: (hsn: string) => deleteHsnGst(hsn),
    onSuccess: () => {
      toast.push('HSN removed', 'success');
      qc.invalidateQueries({ queryKey: ['hsn-gst'] });
    },
    onError: (e) => toast.push(apiError(e, 'Delete failed'), 'error'),
  });

  const openNew = () => setDraft({ hsnCode: '', isNew: true, bands: [{ ...emptyBand }, { ...emptyBand }] });
  const openEdit = (e: HsnGstEntry) =>
    setDraft({
      hsnCode: e.hsnCode,
      isNew: false,
      bands: e.bands.map((b) => ({ minPrice: String(b.minPrice), maxPrice: b.maxPrice == null ? '' : String(b.maxPrice), gstPct: String(b.gstPct) })),
    });

  const canSave = useMemo(() => {
    if (!draft) return false;
    if (!draft.hsnCode.trim()) return false;
    return draft.bands.some((b) => b.gstPct !== '');
  }, [draft]);

  const bandLabel = (b: HsnGstEntry['bands'][number]) =>
    `₹${formatINR(b.minPrice)}${b.maxPrice == null ? '+' : `–₹${formatINR(b.maxPrice)}`} → ${b.gstPct}%`;

  return (
    <div>
      <SectionHeader
        title="HSN GST Master"      />

      <FilterBar>
        <FilterField label="Search" className="sm:w-[300px]">
          <div className="relative">
            <Search size={13} className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search HSN code or description" className="!pl-8" />
          </div>
        </FilterField>
        <Button variant="primary" size="sm" className="sm:ml-auto" icon={<Plus size={12} />} onClick={openNew}>Add HSN</Button>
      </FilterBar>

      <Table<HsnGstEntry>
        storageKey="hsn-gst-master"
        title="HSN GST Slabs"
        count={`${filteredRows.length} HSN code${filteredRows.length === 1 ? '' : 's'}`}
        rows={filteredRows}
        rowKey={(r) => r.hsnCode}
        loading={query.isFetching && rows.length === 0}
        empty="No HSN GST slabs yet — add one to override the item-master GST for that HSN."
        columns={[
          { key: 'hsn', header: 'HSN Code', width: '140px', cell: (r) => <strong className="font-mono">{r.hsnCode}</strong> },
          { key: 'desc', header: 'Description', width: '220px', cell: (r) => <span className={r.description ? '' : 'text-[var(--text-muted)]'}>{r.description || '—'}</span> },
          {
            key: 'bands', header: 'Price Bands → GST%', minWidth: '280px',
            cell: (r) => (
              <div className="flex flex-wrap gap-1">
                {r.bands.map((b, i) => (
                  <span key={i} className="rounded-md border border-accent/20 bg-accent/10 px-2 py-0.5 text-[11px] font-semibold text-accent">
                    {bandLabel(b)}
                  </span>
                ))}
              </div>
            ),
          },
          {
            key: 'actions', header: '', width: '130px', align: 'right', alwaysVisible: true,
            cell: (r) => (
              <div className="flex items-center justify-end gap-1">
                <Button variant="glass" size="sm" onClick={() => openEdit(r)}>Edit</Button>
                <button type="button" onClick={async () => { if (await confirmDlg(`Remove all GST bands for HSN ${r.hsnCode}?`)) delMut.mutate(r.hsnCode); }} className="rounded p-1.5 text-danger hover:bg-danger/10" title="Remove HSN"><Trash2 size={14} /></button>
              </div>
            ),
          },
        ]}
      />

      {draft && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" onClick={() => setDraft(null)}>
          <div className="w-full max-w-xl rounded-xl border border-[var(--border-color)] shadow-2xl" style={{ background: 'var(--bg-primary)' }} onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-[var(--border-color)] px-5 py-4">
              <h2 className="text-base font-bold text-[var(--text-primary)]">{draft.isNew ? 'Add HSN GST Slabs' : `Edit HSN ${draft.hsnCode}`}</h2>
              <button onClick={() => setDraft(null)} className="text-[var(--text-muted)] hover:text-[var(--text-primary)]"><X size={18} /></button>
            </div>
            <div className="space-y-4 p-5">
              <div>
                <label className="mb-1 block text-xs font-semibold text-[var(--text-secondary)]">HSN Code</label>
                <Input
                  value={draft.hsnCode}
                  onChange={(e) => setDraft({ ...draft, hsnCode: e.target.value })}
                  disabled={!draft.isNew}
                  placeholder="e.g. 57023110"
                  maxLength={8}
                />
              </div>

              <div>
                <div className="mb-1 flex items-center justify-between">
                  <label className="text-xs font-semibold text-[var(--text-secondary)]">Price Bands (per-unit, ex-GST)</label>
                  <button type="button" onClick={() => setDraft({ ...draft, bands: [...draft.bands, { ...emptyBand }] })} className="text-[11px] font-semibold text-accent hover:underline">+ Add band</button>
                </div>
                <div className="grid grid-cols-[1fr_1fr_1fr_28px] gap-2 text-[10px] font-bold uppercase tracking-wide text-[var(--text-muted)]">
                  <span>Min ₹</span><span>Max ₹ (blank = ∞)</span><span>GST %</span><span />
                </div>
                <div className="mt-1 space-y-1.5">
                  {draft.bands.map((b, i) => (
                    <div key={i} className="grid grid-cols-[1fr_1fr_1fr_28px] items-center gap-2">
                      <Input type="number" value={b.minPrice} onChange={(e) => { const bands = [...draft.bands]; bands[i] = { ...b, minPrice: e.target.value }; setDraft({ ...draft, bands }); }} />
                      <Input type="number" value={b.maxPrice} onChange={(e) => { const bands = [...draft.bands]; bands[i] = { ...b, maxPrice: e.target.value }; setDraft({ ...draft, bands }); }} placeholder="∞" />
                      <Input type="number" value={b.gstPct} onChange={(e) => { const bands = [...draft.bands]; bands[i] = { ...b, gstPct: e.target.value }; setDraft({ ...draft, bands }); }} />
                      <button type="button" onClick={() => setDraft({ ...draft, bands: draft.bands.filter((_, j) => j !== i) })} className="rounded p-1 text-danger hover:bg-danger/10" title="Remove band"><Trash2 size={13} /></button>
                    </div>
                  ))}
                </div>
                <p className="mt-2 text-[11px] text-[var(--text-muted)]">
                  A price on a boundary falls into the lower band. Example: <strong>0 – 2500 → 5%</strong> and <strong>2500 – ∞ → 18%</strong> means ₹2,500 gets 5%.
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-2 border-t border-[var(--border-color)] px-5 py-4">
              <Button variant="glass" onClick={() => setDraft(null)}>Cancel</Button>
              <Button variant="primary" disabled={!canSave || saveMut.isPending} onClick={() => draft && saveMut.mutate(draft)}>
                {saveMut.isPending ? 'Saving…' : 'Save'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
